<template>
  <div
    class="relative h-[100dvh] w-full bg-zinc-50 text-zinc-950 transition-colors duration-500 dark:bg-black dark:text-zinc-50 font-sans selection:bg-brand-500/30 overflow-hidden">

    <div class="relative z-10 mx-auto max-w-[1400px] p-2 sm:p-4 lg:p-6 h-full flex flex-col">
      <section
        class="flex flex-col flex-1 overflow-hidden rounded-2xl sm:rounded-[32px] border border-zinc-200 bg-white/80 shadow-2xl backdrop-blur-xl transition-all duration-500 dark:border-zinc-800 dark:bg-[#09090b]/80 dark:shadow-black/80">

        <header
          class="relative z-20 shrink-0 border-b border-zinc-200/50 bg-white/50 p-3 backdrop-blur-md transition-colors duration-500 dark:border-zinc-800/50 dark:bg-zinc-900/50 sm:p-6">
          <div
            class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between animate-in fade-in slide-in-from-top-4 duration-700">
            <div class="flex items-center justify-between sm:justify-start gap-3 sm:gap-4">
              <div class="flex items-center gap-3">
                <div
                  class="group relative grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-400 text-white shadow-lg shadow-brand-500/20 transition-all duration-500 hover:scale-110 hover:shadow-brand-500/40 sm:h-14 sm:w-14 sm:rounded-[20px]">
                  <div
                    class="absolute inset-0 rounded-2xl bg-white/20 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  </div>
                  <span
                    class="material-symbols-rounded text-[20px] sm:text-[28px] transition-transform duration-500 group-hover:rotate-12">auto_awesome</span>
                </div>

                <div>
                  <div class="flex flex-wrap items-center gap-2">
                    <h1
                      class="bg-gradient-to-r from-zinc-900 to-zinc-600 bg-clip-text text-[16px] sm:text-[24px] font-[800] tracking-tight text-transparent dark:from-white dark:to-zinc-400">
                      Color Advisor
                    </h1>
                    <span
                      class="inline-flex items-center rounded-full border border-brand-500/20 bg-brand-500/5 px-2 py-0.5 text-[9px] sm:text-[11px] font-bold uppercase tracking-widest text-brand-600 backdrop-blur-sm dark:border-brand-400/20 dark:bg-brand-400/10 dark:text-brand-400">
                      Premium
                    </span>
                  </div>

                  <div
                    class="mt-0.5 sm:mt-1 flex flex-wrap items-center gap-2 text-[10px] sm:text-[12px] font-medium text-zinc-500 dark:text-zinc-400">
                    <span class="relative inline-flex items-center gap-1.5">
                      <span class="relative flex h-1.5 w-1.5 sm:h-2 sm:w-2">
                        <span
                          class="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-400 opacity-75"></span>
                        <span class="relative inline-flex h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full bg-brand-500"></span>
                      </span>
                      AI Online
                    </span>
                  </div>
                </div>
              </div>

              <button v-if="history.length" type="button" @click="refreshHistory"
                class="sm:hidden group grid h-8 w-8 place-items-center rounded-lg border border-zinc-200 bg-white text-zinc-500 shadow-sm transition-all duration-300 hover:border-brand-500/30 hover:text-brand-600 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400">
                <span class="material-symbols-rounded text-[18px]">refresh</span>
              </button>
            </div>

            <div class="hidden sm:flex sm:items-center sm:gap-2">

              <router-link to="/dashboard/hub"
                class="group grid h-10 w-10 place-items-center rounded-xl border border-zinc-200 bg-white text-zinc-500 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-brand-500/30 hover:text-brand-600 hover:shadow-md dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400 dark:hover:border-brand-400/30 dark:hover:text-brand-400"
                title="Tutorial Hub">
                <span
                  class="material-symbols-rounded text-[20px] transition-transform duration-500 group-hover:rotate-12">school</span>
              </router-link>

              <button v-if="history.length" type="button" @click="refreshHistory"
                class="group grid h-10 w-10 place-items-center rounded-xl border border-zinc-200 bg-white text-zinc-500 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-brand-500/30 hover:text-brand-600 hover:shadow-md dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400 dark:hover:border-brand-400/30 dark:hover:text-brand-400">
                <span
                  class="material-symbols-rounded text-[20px] transition-transform duration-500 group-hover:rotate-180">refresh</span>
              </button>
            </div>
          </div>

          <div class="mt-4 sm:mt-6">
            <div class="relative group">
              <div
                class="absolute -inset-0.5 rounded-[20px] bg-gradient-to-r from-brand-500 to-purple-600 opacity-0 blur transition duration-500 group-focus-within:opacity-25">
              </div>
              <div
                class="relative flex overflow-hidden rounded-[18px] border border-zinc-200 bg-white shadow-sm transition-all duration-300 dark:border-zinc-800 dark:bg-zinc-950/50">
                <div class="flex-1 p-1 min-w-0">
                  <div class="no-scrollbar flex gap-2 overflow-x-auto p-2">
                    <button
                      v-for="(suggestion, idx) in ['Audit my palette', 'Give 3 brand blues', 'Fix contrast issues']"
                      :key="idx" @click="question = suggestion"
                      class="animate-enter whitespace-nowrap rounded-lg bg-zinc-50 px-2.5 py-1.5 text-[10px] sm:text-[11px] font-medium text-zinc-600 transition-all duration-300 hover:bg-brand-50 hover:text-brand-600 active:scale-95 dark:bg-zinc-900 dark:text-zinc-400 dark:hover:bg-brand-900/20 dark:hover:text-brand-400"
                      :style="{ animationDelay: `${idx * 100}ms` }">
                      {{ suggestion }}
                    </button>
                  </div>

                  <div class="relative px-3 pb-2">
                    <textarea v-model="question" rows="1"
                      class="min-h-[44px] sm:min-h-[48px] w-full resize-none bg-transparent py-2 sm:py-3 text-[14px] sm:text-[15px] text-zinc-900 placeholder:text-zinc-400 focus:outline-none dark:text-zinc-100 dark:placeholder:text-zinc-600"
                      placeholder="Type a request..." @keydown.enter.prevent="ask"></textarea>

                    <div
                      class="hidden sm:flex items-center justify-between border-t border-zinc-100 pt-2 dark:border-zinc-800/50">
                      <div class="flex items-center gap-3">
                        <span
                          class="flex items-center gap-1.5 text-[10px] font-medium text-zinc-400 dark:text-zinc-500">
                          <span class="material-symbols-rounded text-[14px]">keyboard_return</span>
                          Send
                        </span>
                        <span
                          class="flex items-center gap-1.5 text-[10px] font-medium text-zinc-400 dark:text-zinc-500">
                          <span class="material-symbols-rounded text-[14px]">markdown</span>
                          MD
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="flex flex-col justify-end p-2">
                  <button type="button" @click="ask" :disabled="loading || !question.trim()"
                    class="group relative grid h-9 w-9 sm:h-10 sm:w-10 place-items-center rounded-xl bg-zinc-900 text-white shadow-lg transition-all duration-300 hover:scale-105 hover:bg-brand-600 hover:shadow-brand-500/25 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100 disabled:hover:bg-zinc-900 dark:bg-white dark:text-zinc-900 dark:hover:bg-brand-400">
                    <span v-if="loading"
                      class="material-symbols-rounded animate-spin text-[18px] sm:text-[20px]">progress_activity</span>
                    <span v-else
                      class="material-symbols-rounded text-[18px] sm:text-[20px] transition-transform duration-300 group-hover:-rotate-45">arrow_upward</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </header>

        <main
          class="relative z-10 flex flex-1 flex-col gap-4 overflow-hidden p-2 sm:p-4 lg:flex-row lg:p-6 lg:gap-6 min-h-0">

          <section
            class="flex flex-1 flex-col overflow-hidden rounded-2xl border border-zinc-200 bg-white/50 shadow-sm backdrop-blur-sm transition-all duration-300 dark:border-zinc-800 dark:bg-zinc-900/30 min-h-0">
            <div
              class="flex shrink-0 items-center justify-between border-b border-zinc-200/50 p-3 sm:p-4 dark:border-zinc-800/50">
              <div class="flex items-center gap-2.5">
                <div
                  class="grid h-7 w-7 sm:h-8 sm:w-8 place-items-center rounded-lg bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
                  <span class="material-symbols-rounded text-[16px] sm:text-[18px]">history</span>
                </div>
                <div>
                  <h2 class="text-[12px] sm:text-[13px] font-bold text-zinc-900 dark:text-zinc-100">Timeline</h2>
                  <p class="text-[10px] sm:text-[11px] text-zinc-500 dark:text-zinc-500">Recent consultations</p>
                </div>
              </div>
              <span
                class="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-bold text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">{{
                  sortedHistory.length }}</span>
            </div>

            <div class="relative flex-1 overflow-hidden">
              <transition name="fade">
                <div v-if="historyLoading"
                  class="absolute inset-0 z-20 grid place-items-center bg-white/50 backdrop-blur-sm dark:bg-zinc-950/50">
                  <div class="flex flex-col items-center gap-3">
                    <span class="material-symbols-rounded animate-spin text-2xl text-brand-500">progress_activity</span>
                    <span class="text-xs font-medium text-zinc-500">Syncing...</span>
                  </div>
                </div>
              </transition>

              <div class="h-full overflow-y-auto p-3 sm:p-4 custom-scrollbar">
                <transition-group name="list" tag="div" class="space-y-3 sm:space-y-4">
                  <article v-for="item in sortedHistory" :key="item.id"
                    class="group relative grid gap-3 rounded-2xl border border-zinc-200 bg-white p-3 sm:p-4 shadow-sm transition-all duration-300 hover:border-brand-500/30 hover:shadow-lg hover:shadow-brand-500/5 dark:border-zinc-800 dark:bg-zinc-900 dark:hover:border-brand-400/30">
                    <div class="flex items-start justify-between gap-3">
                      <div class="flex items-start gap-3">
                        <div
                          class="mt-1 grid h-7 w-7 sm:h-8 sm:w-8 shrink-0 place-items-center rounded-full bg-brand-50 text-brand-600 ring-4 ring-brand-50/50 dark:bg-brand-500/10 dark:text-brand-400 dark:ring-brand-500/5">
                          <span class="material-symbols-rounded text-[14px] sm:text-[16px]">person</span>
                        </div>
                        <div>
                          <div
                            class="text-[12px] sm:text-[13px] font-medium leading-relaxed text-zinc-800 dark:text-zinc-200">
                            {{ item.question }}
                          </div>
                          <div class="mt-1 flex items-center gap-2">
                            <span class="text-[10px] font-medium text-zinc-400">{{ formatDate(item.createdAt) }}</span>
                          </div>
                        </div>
                      </div>

                      <button
                        class="opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity duration-200 text-zinc-400 hover:text-red-500 dark:text-zinc-600 dark:hover:text-red-400"
                        @click.stop="openDeleteModal(item)">
                        <span class="material-symbols-rounded text-[16px] sm:text-[18px]">delete</span>
                      </button>
                    </div>

                    <div class="relative pl-0 sm:pl-11">
                      <div
                        class="relative overflow-hidden rounded-xl border border-zinc-100 bg-zinc-50/80 p-3 sm:p-4 transition-colors dark:border-zinc-800/50 dark:bg-zinc-950/50">
                        <div
                          class="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-brand-500 to-purple-500 opacity-50">
                        </div>

                        <div
                          class="proseWrap text-[12px] sm:text-[13px] leading-relaxed text-zinc-600 dark:text-zinc-300"
                          :class="{ 'line-clamp-4 max-h-[100px] sm:max-h-[120px]': !isExpanded(item.id) }"
                          v-html="renderFormattedAnswer(item.answer)"></div>

                        <div v-if="!isExpanded(item.id) && isLongContent(item.answer)"
                          class="absolute inset-x-0 bottom-0 flex h-16 sm:h-20 items-end justify-center bg-gradient-to-t from-zinc-50 via-zinc-50/90 to-transparent pb-2 dark:from-zinc-950 dark:via-zinc-950/90">
                          <button
                            class="flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-[10px] font-bold shadow-sm ring-1 ring-zinc-200 transition-transform hover:scale-105 hover:text-brand-600 dark:bg-zinc-800 dark:ring-zinc-700 dark:hover:text-brand-400"
                            @click="toggleExpand(item.id)">
                            Read more
                            <span class="material-symbols-rounded text-[14px]">expand_more</span>
                          </button>
                        </div>
                      </div>

                      <div v-if="isExpanded(item.id)"
                        class="mt-3 flex justify-end gap-2 animate-in fade-in slide-in-from-top-1 duration-300">
                        <button
                          class="flex items-center gap-1.5 rounded-lg px-2 py-1 text-[10px] font-bold text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
                          @click="toggleExpand(item.id)">
                          Collapse
                          <span class="material-symbols-rounded text-[14px]">expand_less</span>
                        </button>
                        <button
                          class="flex items-center gap-1.5 rounded-lg bg-white px-2.5 py-1 text-[10px] font-bold text-zinc-700 shadow-sm ring-1 ring-zinc-200 transition-all hover:ring-brand-500 dark:bg-zinc-800 dark:text-zinc-200 dark:ring-zinc-700 dark:hover:ring-brand-400"
                          @click="copy(item.answer)">
                          <span class="material-symbols-rounded text-[14px]">content_copy</span>
                          Copy
                        </button>
                      </div>
                    </div>
                  </article>
                </transition-group>

                <div v-if="!historyLoading && !sortedHistory.length"
                  class="flex h-full flex-col items-center justify-center p-4 text-center">
                  <div
                    class="mb-4 grid h-12 w-12 sm:h-16 sm:w-16 place-items-center rounded-[20px] sm:rounded-[24px] bg-zinc-50 shadow-inner dark:bg-zinc-900">
                    <span
                      class="material-symbols-rounded text-2xl sm:text-3xl text-zinc-300 dark:text-zinc-600">auto_awesome</span>
                  </div>
                  <h3 class="text-sm font-bold text-zinc-900 dark:text-zinc-100">No history yet</h3>
                  <p class="mt-1 max-w-[280px] text-xs text-zinc-500 dark:text-zinc-400">
                    Start a conversation to get professional color advice.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <aside class="hidden lg:flex w-80 flex-col gap-4 overflow-hidden shrink-0">
            <div
              class="rounded-2xl border border-zinc-200 bg-white/50 p-1 shadow-sm backdrop-blur-sm dark:border-zinc-800 dark:bg-zinc-900/30">
              <div class="grid grid-cols-2 gap-1">
                <div
                  class="rounded-xl bg-white p-3 shadow-sm transition-transform hover:scale-[1.02] dark:bg-zinc-800/50">
                  <div class="text-[10px] font-bold text-zinc-400">Conversations</div>
                  <div class="mt-1 text-xl font-black tracking-tight text-zinc-900 dark:text-white">{{
                    sortedHistory.length }}</div>
                </div>
                <div
                  class="rounded-xl bg-white p-3 shadow-sm transition-transform hover:scale-[1.02] dark:bg-zinc-800/50">
                  <div class="text-[10px] font-bold text-zinc-400">Mode</div>
                  <div class="mt-1 text-xl font-black tracking-tight text-brand-600 dark:text-brand-400">Pro</div>
                </div>
              </div>
            </div>

            <div
              class="flex-1 overflow-hidden rounded-2xl border border-zinc-200 bg-white/50 shadow-sm backdrop-blur-sm dark:border-zinc-800 dark:bg-zinc-900/30">
              <div class="border-b border-zinc-200/50 p-4 dark:border-zinc-800/50">
                <div class="flex items-center gap-2">
                  <span class="material-symbols-rounded text-[18px] text-brand-500">dashboard</span>
                  <span class="text-[13px] font-bold text-zinc-900 dark:text-zinc-100">Advisor Board</span>
                </div>
              </div>

              <div class="grid gap-3 p-4">
                <div
                  class="group cursor-default rounded-xl border border-brand-500/20 bg-brand-50/50 p-3 transition-all hover:bg-brand-50 dark:border-brand-400/20 dark:bg-brand-500/5 dark:hover:bg-brand-500/10">
                  <div class="flex gap-3">
                    <div
                      class="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-brand-100 text-brand-600 transition-colors group-hover:bg-brand-200 dark:bg-brand-900/30 dark:text-brand-300">
                      <span class="material-symbols-rounded text-[18px]">verified</span>
                    </div>
                    <div>
                      <div class="text-[12px] font-bold text-zinc-900 dark:text-zinc-100">Production Ready</div>
                      <div class="mt-0.5 text-[11px] text-zinc-600 dark:text-zinc-400">Tokens & accessibility first.
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  v-for="(card, i) in [{ icon: 'palette', title: 'Palette Critique', desc: 'Harmony & saturation checks' }, { icon: 'contrast', title: 'WCAG Check', desc: 'AA/AAA compliance ratios' }]"
                  :key="i"
                  class="group cursor-default rounded-xl border border-zinc-200 bg-white p-3 transition-all hover:-translate-y-0.5 hover:shadow-md dark:border-zinc-800 dark:bg-zinc-900 dark:hover:border-zinc-700">
                  <div class="flex gap-3">
                    <div
                      class="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-zinc-50 text-zinc-500 transition-colors group-hover:bg-zinc-100 group-hover:text-zinc-900 dark:bg-zinc-800 dark:text-zinc-400 dark:group-hover:bg-zinc-700 dark:group-hover:text-zinc-100">
                      <span class="material-symbols-rounded text-[18px]">{{ card.icon }}</span>
                    </div>
                    <div>
                      <div class="text-[12px] font-bold text-zinc-900 dark:text-zinc-100">{{ card.title }}</div>
                      <div class="mt-0.5 text-[11px] text-zinc-500 dark:text-zinc-400">{{ card.desc }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </main>

        <transition name="scale">
          <div v-if="showDeleteModal && itemToDelete"
            class="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4 backdrop-blur-sm transition-all duration-300">
            <div
              class="w-full max-w-[320px] sm:max-w-[400px] scale-100 overflow-hidden rounded-[24px] border border-zinc-200 bg-white shadow-2xl transition-all dark:border-zinc-800 dark:bg-zinc-900">
              <div class="flex flex-col items-center gap-4 p-6 text-center">
                <div
                  class="grid h-12 w-12 sm:h-14 sm:w-14 place-items-center rounded-full bg-red-50 text-red-500 dark:bg-red-500/10">
                  <span class="material-symbols-rounded text-[24px] sm:text-[28px]">delete_forever</span>
                </div>
                <div>
                  <h3 class="text-[15px] sm:text-[16px] font-bold text-zinc-900 dark:text-zinc-100">Delete conversation?
                  </h3>
                  <p class="mt-2 text-[12px] sm:text-[13px] leading-relaxed text-zinc-500 dark:text-zinc-400">
                    This action is permanent and cannot be undone. Are you sure?
                  </p>
                </div>
              </div>

              <div
                class="grid grid-cols-2 gap-3 border-t border-zinc-100 bg-zinc-50/50 p-4 dark:border-zinc-800 dark:bg-zinc-950/50">
                <button
                  class="rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-[13px] font-bold text-zinc-700 transition-transform active:scale-95 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300"
                  @click="closeDeleteModal">
                  Cancel
                </button>
                <button
                  class="flex items-center justify-center gap-2 rounded-xl bg-red-500 px-4 py-2.5 text-[13px] font-bold text-white shadow-lg shadow-red-500/20 transition-transform active:scale-95 hover:bg-red-600"
                  @click="confirmDelete">
                  <span v-if="deleteLoading"
                    class="material-symbols-rounded animate-spin text-[16px]">progress_activity</span>
                  <span v-else>Delete</span>
                </button>
              </div>
            </div>
          </div>
        </transition>
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import axios from 'axios'
import MarkdownIt from 'markdown-it'

const question = ref('')
const loading = ref(false)
const history = ref([])
const historyLoading = ref(false)
const showDeleteModal = ref(false)
const itemToDelete = ref(null)
const deleteLoading = ref(false)
const expandedItems = ref(new Set())
const isDark = ref(false)

const md = new MarkdownIt({ html: true, breaks: true, linkify: true })

const sortedHistory = computed(() => {
  if (!history.value?.length) return []
  return [...history.value].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
})

function applyTheme(dark) {
  isDark.value = !!dark
  const root = document.documentElement
  if (isDark.value) root.classList.add('dark')
  else root.classList.remove('dark')
  localStorage.setItem('theme', isDark.value ? 'dark' : 'light')
}

async function ask() {
  if (!question.value.trim()) return
  loading.value = true
  const currentQ = question.value
  question.value = ''

  try {
    await axios.post('https://backend.nexgridco.com/color-ai/ask', { question: currentQ })
    await refreshHistory()
  } catch (err) {
    console.error(err)
    question.value = currentQ
  } finally {
    loading.value = false
  }
}

async function fetchHistory() {
  historyLoading.value = true
  try {
    const { data } = await axios.get('/color-ai/history/advisor')
    history.value = Array.isArray(data) ? data : []
  } catch {
    history.value = []
  } finally {
    historyLoading.value = false
  }
}

async function refreshHistory() {
  await fetchHistory()
}

function renderFormattedAnswer(text) {
  if (!text) return ''
  const html = md.render(text)
  return html.replace(/#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})\b/g, (match) => {
    return `
      <span class="inline-flex select-all items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-2 py-0.5 align-middle shadow-sm transition-transform hover:scale-105 dark:border-zinc-700 dark:bg-zinc-800">
        <span class="h-2.5 w-2.5 rounded-full border border-black/10 dark:border-white/10" style="background-color:${match}"></span>
        <span class="font-mono text-[10px] font-bold tracking-wide text-zinc-700 dark:text-zinc-200">${match}</span>
      </span>
    `
  })
}

function isLongContent(text) {
  return text && text.length > 350
}

function isExpanded(id) {
  return expandedItems.value.has(id)
}

function toggleExpand(id) {
  if (expandedItems.value.has(id)) expandedItems.value.delete(id)
  else expandedItems.value.add(id)
}

function copy(text) {
  if (!text) return
  navigator.clipboard.writeText(text)
}

function formatDate(dateStr) {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function openDeleteModal(item) {
  itemToDelete.value = item
  showDeleteModal.value = true
}

function closeDeleteModal() {
  if (deleteLoading.value) return
  showDeleteModal.value = false
  itemToDelete.value = null
}

async function confirmDelete() {
  if (!itemToDelete.value) return
  deleteLoading.value = true
  try {
    await axios.delete(`/color-ai/history/advisor/${itemToDelete.value.id}`)
    history.value = history.value.filter(h => h.id !== itemToDelete.value.id)
  } finally {
    deleteLoading.value = false
    closeDeleteModal()
  }
}

onMounted(() => {
  const saved = localStorage.getItem('theme')
  if (saved === 'dark') applyTheme(true)
  else if (saved === 'light') applyTheme(false)
  else applyTheme(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches)

  fetchHistory()
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@500&display=swap');

.font-sans {
  font-family: 'Inter', sans-serif;
}

.font-mono {
  font-family: 'JetBrains Mono', monospace;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 5px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(161, 161, 170, 0.2);
  border-radius: 999px;
}

.custom-scrollbar:hover::-webkit-scrollbar-thumb {
  background-color: rgba(161, 161, 170, 0.4);
}

.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.proseWrap :deep(h1),
.proseWrap :deep(h2),
.proseWrap :deep(h3) {
  font-weight: 800;
  letter-spacing: -0.02em;
  margin: 16px 0 8px;
  color: inherit;
}

.proseWrap :deep(p) {
  margin: 8px 0;
}

.proseWrap :deep(ul),
.proseWrap :deep(ol) {
  margin: 8px 0;
  padding-left: 20px;
  list-style-type: disc;
}

.proseWrap :deep(li) {
  margin: 4px 0;
}

.proseWrap :deep(strong) {
  font-weight: 700;
  color: inherit;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.list-enter-active,
.list-leave-active {
  transition: all 0.4s cubic-bezier(0.25, 1, 0.5, 1);
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(-15px);
}

.list-move {
  transition: transform 0.4s ease;
}

.scale-enter-active,
.scale-leave-active {
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.scale-enter-from,
.scale-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

@keyframes blob {
  0% {
    transform: translate(0px, 0px) scale(1);
  }

  33% {
    transform: translate(30px, -50px) scale(1.1);
  }

  66% {
    transform: translate(-20px, 20px) scale(0.9);
  }

  100% {
    transform: translate(0px, 0px) scale(1);
  }
}

.animate-blob {
  animation: blob 10s infinite;
}

@keyframes enter {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-enter {
  animation: enter 0.6s cubic-bezier(0.16, 1, 0.3, 1) backwards;
}

.material-symbols-rounded {
  font-variation-settings: 'FILL' 0, 'wght' 500, 'GRAD' 0, 'opsz' 24;
}
</style>